import React, { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";

interface SlideoutProps {
  children: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  slideFrom?: "left" | "right"; // Optional prop to determine slide direction
  maxWidth?: string;
}

const SlideOut = ({ children, open, setOpen, slideFrom = "right", maxWidth = "max-w-md" }: SlideoutProps) => {
  // Determine slide-in and slide-out starting points
  const slideInFrom = slideFrom === "left" ? "-translate-x-full" : "translate-x-full";
  const slideOutTo = slideFrom === "left" ? "-translate-x-full" : "translate-x-full";
  // Determine panel positioning and padding
  const positionClass = slideFrom === "left" ? "left-0" : "right-0";

  let emptyRef = useRef(null);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 tw-boundary"
        initialFocus={emptyRef}
        onClose={() => setOpen(false)}
      >
        <div className="fixed inset-0 overflow-hidden z-10">
          <div className="absolute inset-0 overflow-hidden">
            {/* Apply dynamic positioning and padding based on slide direction */}
            <div className={`pointer-events-none fixed inset-y-0 ${positionClass} flex max-w-full`}>
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom={slideInFrom}
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo={slideOutTo}
              >
                <Dialog.Panel className={`pointer-events-auto w-screen ${maxWidth}`}>
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    {/* Dummy element that can take initial focus, otherwise the Headless UI <Dialog> component will set autofocus to the first input in the slide-out, which we don't want */}
                    <div ref={emptyRef} className="hidden" />
                    <div className="h-full">
                      {children}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SlideOut;
